import type { CapturedFrame, FrameSource } from '../../visual/index.js';
import type { RenderingPrototype } from '../prototype/RenderingPrototype.js';

/**
 * Getting the drawn frame out of the canvas, without PNG.
 *
 * Phase 5 used `canvas.toBlob('image/png')` and measured 0.97 frames per second.
 * Almost none of that was the artwork: PNG is a lossless, entropy-coded,
 * per-frame format, and encoding one 1080p frame of it costs more than drawing
 * the frame did. It also throws away the one thing a video encoder wants, which
 * is a picture already sitting on the GPU.
 *
 * Two ways out, and both are implemented here because the right one is a
 * question about the machine rather than about the code:
 *
 *   - **`VideoFrame(canvas)`** wraps the canvas's current contents directly.
 *     There is no pixel copy in user space at all; the frame stays where the
 *     renderer left it and the encoder may be able to take it without a round
 *     trip through main memory.
 *   - **`readPixels` → `VideoFrame(buffer)`** pulls the pixels back into an
 *     `ArrayBuffer` and builds a frame from that. It is a genuine copy of every
 *     byte and it stalls the pipeline while the GPU catches up, but it works
 *     where the first does not and it is the only one of the two that can be
 *     inspected, which makes it useful for checking that a frame is not black.
 *
 * `benchmark` runs both so the choice is made on measurements.
 *
 * ## The vertical flip
 *
 * WebGL's origin is bottom-left and a `VideoFrame`'s is top-left. Wrapping the
 * canvas gets this right for free, because the canvas is already the right way
 * up as far as the compositor is concerned. Reading pixels does not: `readPixels`
 * hands back rows starting at the bottom, so the buffer path flips them. Without
 * that the two paths disagree, which is exactly the sort of difference that
 * survives a test suite and shows up in an exported file.
 */

/** What the browser must provide. Held as types so the core never sees them. */
interface VideoFrameLike {
  close(): void;
  readonly codedWidth: number;
  readonly codedHeight: number;
}

interface VideoFrameConstructor {
  new (source: CanvasImageSource, init: { timestamp: number; duration?: number }): VideoFrameLike;
  new (
    data: ArrayBufferView,
    init: {
      format: string;
      codedWidth: number;
      codedHeight: number;
      timestamp: number;
      duration?: number;
    },
  ): VideoFrameLike;
}

export type CaptureMethod = 'videoframe-canvas' | 'readpixels-buffer';

/** A captured frame that also carries the browser object the encoder needs. */
export interface CanvasCapturedFrame extends CapturedFrame {
  /** The `VideoFrame`. Typed loosely so `src/visual` never has to know it. */
  readonly native: unknown;
}

export interface CanvasFrameSourceOptions {
  readonly prototype: RenderingPrototype;
  readonly method?: CaptureMethod;
  /** Microseconds. Written onto each frame; the encoder may use it. */
  readonly frameDurationMicros?: number;
}

export class CanvasFrameSource implements FrameSource {
  private readonly prototype: RenderingPrototype;
  private readonly duration: number;
  private open = 0;
  private captured = 0;
  private failures = 0;
  private buffer: Uint8Array | null = null;
  private flipped: Uint8Array | null = null;

  readonly method: CaptureMethod;

  constructor(options: CanvasFrameSourceOptions) {
    this.prototype = options.prototype;
    this.method = options.method ?? 'videoframe-canvas';
    this.duration = options.frameDurationMicros ?? 0;
  }

  /** Whether this browser can build a `VideoFrame` at all. */
  static get available(): boolean {
    return typeof (globalThis as { VideoFrame?: unknown }).VideoFrame === 'function';
  }

  get liveFrames(): number {
    return this.open;
  }

  async capture(index: number, timestampMicros: number): Promise<CapturedFrame | null> {
    const Frame = (globalThis as { VideoFrame?: VideoFrameConstructor }).VideoFrame;
    if (Frame === undefined) return null;

    try {
      const native =
        this.method === 'readpixels-buffer'
          ? this.fromPixels(Frame, timestampMicros)
          : this.fromCanvas(Frame, timestampMicros);
      if (native === null) return null;

      this.open += 1;
      this.captured += 1;
      let closed = false;
      const source = this;
      const frame: CanvasCapturedFrame = {
        index,
        timestampMicros,
        native,
        close(): void {
          // Guarded because a double close throws in some engines, and the
          // export closes a frame on the failure path as well as the happy one.
          if (closed) return;
          closed = true;
          source.open -= 1;
          native.close();
        },
      };
      return frame;
    } catch {
      // A capture that fails is a dropped frame, not a dead export. The count is
      // reported, so a run that silently dropped half its frames is visible.
      this.failures += 1;
      return null;
    }
  }

  private fromCanvas(Frame: VideoFrameConstructor, timestamp: number): VideoFrameLike | null {
    const canvas = this.prototype.stage.renderer.domElement;
    return new Frame(canvas as unknown as CanvasImageSource, {
      timestamp,
      ...(this.duration > 0 ? { duration: this.duration } : {}),
    });
  }

  /**
   * Reads the framebuffer back and builds a frame from the bytes.
   *
   * Both scratch buffers are kept between frames. Allocating 8 MB per frame at
   * sixty frames a second is 480 MB a second of garbage, and the collector
   * pauses that causes are longer than the encode.
   */
  private fromPixels(Frame: VideoFrameConstructor, timestamp: number): VideoFrameLike | null {
    const renderer = this.prototype.stage.renderer;
    const gl = renderer.getContext();
    const width = renderer.domElement.width;
    const height = renderer.domElement.height;
    const bytes = width * height * 4;

    if (this.buffer === null || this.buffer.length !== bytes) {
      this.buffer = new Uint8Array(new ArrayBuffer(bytes));
      this.flipped = new Uint8Array(new ArrayBuffer(bytes));
    }
    const raw = this.buffer;
    const out = this.flipped as Uint8Array;
    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, raw);

    // Bottom-up to top-down. See the note on the class.
    const stride = width * 4;
    for (let y = 0; y < height; y += 1) {
      const from = (height - 1 - y) * stride;
      out.set(raw.subarray(from, from + stride), y * stride);
    }

    return new Frame(out, {
      format: 'RGBA',
      codedWidth: width,
      codedHeight: height,
      timestamp,
      ...(this.duration > 0 ? { duration: this.duration } : {}),
    });
  }

  /**
   * The pixels of the current frame, top-down RGBA.
   *
   * Not part of the capture path — this is for checking that what is about to be
   * encoded is not black, which §23 asks for and which no amount of counting
   * frames can answer.
   */
  readPixels(): { width: number; height: number; data: Uint8Array } {
    const renderer = this.prototype.stage.renderer;
    const gl = renderer.getContext();
    const width = renderer.domElement.width;
    const height = renderer.domElement.height;
    const raw = new Uint8Array(new ArrayBuffer(width * height * 4));
    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, raw);
    const out = new Uint8Array(new ArrayBuffer(raw.length));
    const stride = width * 4;
    for (let y = 0; y < height; y += 1) {
      const from = (height - 1 - y) * stride;
      out.set(raw.subarray(from, from + stride), y * stride);
    }
    return { width, height, data: out };
  }

  diagnostics(): {
    readonly method: CaptureMethod;
    readonly captured: number;
    readonly liveFrames: number;
    readonly failures: number;
  } {
    return {
      method: this.method,
      captured: this.captured,
      liveFrames: this.open,
      failures: this.failures,
    };
  }

  dispose(): void {
    this.buffer = null;
    this.flipped = null;
  }
}

/**
 * Times both capture paths on whatever is currently on the canvas.
 *
 * Deliberately measures capture *alone*: it does not draw and it does not
 * encode, so the number is the cost of getting one frame out and nothing else.
 * Frames are closed immediately, which is also a check that closing works — a
 * leak here shows up as a stall within a few hundred iterations.
 */
export async function benchmarkCapture(
  prototype: RenderingPrototype,
  options: { readonly frames?: number } = {},
): Promise<
  readonly {
    readonly method: CaptureMethod | 'toBlob-png';
    readonly frames: number;
    readonly totalMs: number;
    readonly perFrameMs: number;
    readonly framesPerSecond: number;
    readonly bytesPerFrame: number | null;
    readonly error: string | null;
  }[]
> {
  const frames = Math.max(1, options.frames ?? 30);
  const results = [];

  for (const method of ['videoframe-canvas', 'readpixels-buffer'] as const) {
    const source = new CanvasFrameSource({ prototype, method });
    let error: string | null = null;
    const started = performance.now();
    let taken = 0;
    for (let index = 0; index < frames; index += 1) {
      const frame = await source.capture(index, index * 1000);
      if (frame === null) {
        error = 'capture returned null';
        break;
      }
      frame.close();
      taken += 1;
    }
    const total = performance.now() - started;
    source.dispose();
    results.push({
      method,
      frames: taken,
      totalMs: Number(total.toFixed(2)),
      perFrameMs: Number((total / Math.max(1, taken)).toFixed(3)),
      framesPerSecond: Number((taken / (total / 1000)).toFixed(2)),
      bytesPerFrame: null,
      error,
    });
  }

  // The old path, measured the same way, so the comparison in the report is
  // like for like rather than against a number from a previous phase.
  {
    const canvas = prototype.stage.renderer.domElement;
    const started = performance.now();
    let taken = 0;
    let bytes = 0;
    let error: string | null = null;
    for (let index = 0; index < frames; index += 1) {
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((result) => resolve(result), 'image/png');
      });
      if (blob === null) {
        error = 'toBlob returned null';
        break;
      }
      bytes += blob.size;
      taken += 1;
    }
    const total = performance.now() - started;
    results.push({
      method: 'toBlob-png' as const,
      frames: taken,
      totalMs: Number(total.toFixed(2)),
      perFrameMs: Number((total / Math.max(1, taken)).toFixed(3)),
      framesPerSecond: Number((taken / (total / 1000)).toFixed(2)),
      bytesPerFrame: taken > 0 ? Math.round(bytes / taken) : null,
      error,
    });
  }

  return results;
}
